/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import type Router from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ContentImportService from 'embercom/services/content-import-service';
import type Folder from 'embercom/models/content-service/folder';
import type Store from '@ember-data/store';
import KnowledgeHubApi, {
  type KnowledgeHubApiQueryParams,
} from 'embercom/lib/knowledge-hub/list-api';
import { getOwner } from '@ember/application';
import type Transition from '@ember/routing/transition';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import { type ContentSearchParams } from 'embercom/lib/content-service/search-api';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type FolderController from 'embercom/controllers/apps/app/automation/fin-ai-agent/content-folder';

type ModelParams = {
  folder_id: string;
};

export default class AutomationFinAIAgentContentFolderRoute extends Route {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare contentImportService: ContentImportService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare router: Router;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.content-folder',
  };

  protected get contentRoute(): string {
    return 'apps.app.automation.fin-ai-agent.content';
  }

  async beforeModel(transition: Transition) {
    if (!this.appService.app.canUseUnifiedContentUI) {
      this.router.transitionTo(this.contentRoute);
      return;
    }

    // usageSummary fetch is skipped when cached — the endpoint is slow
    // (20-30s on large workspaces, see aiAgentSetupService for context).
    await Promise.all([
      this.knowledgeHubService.usageSummary
        ? undefined
        : this.knowledgeHubService.fetchKnowledgeUsageSummary(),
      this.contentImportService.ensureContentImportSourcesLoaded(),
    ]);

    return super.beforeModel(transition);
  }

  async model(params: ModelParams): Promise<Folder> {
    return await this.getFolder(params.folder_id);
  }

  async afterModel(folder: Folder): Promise<KnowledgeHubApi> {
    if (!folder?.id) {
      this.router.transitionTo(this.contentRoute);
    }

    let defaultSearchParams: ContentSearchParams = {
      object_types: this.knowledgeHubService.getSearchObjectTypes(),
      content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
      app_id: this.appService.app.id as string,
      folder_ids: [folder.id],
      folder_entity_type: EntityType.ContentLibraryFolder,
      sort_by: 'title',
      sort_direction: 'asc' as SortDirection,
    };

    let folderQueryParams = this.paramsFor(this.routeName) as KnowledgeHubApiQueryParams;

    this.knowledgeHubService.api = new KnowledgeHubApi(
      getOwner(this),
      defaultSearchParams,
      folderQueryParams,
    );
    this.knowledgeHubService.api.loadPage();
    return this.knowledgeHubService.api;
  }

  async setupController(controller: any, folder: Folder, transition: Transition) {
    super.setupController(controller, folder, transition);
    controller.setProperties({ listApi: this.knowledgeHubService.api, folder });
  }

  async getFolder(folder_id: string): Promise<Folder> {
    await this.knowledgeHubService.fetchFoldersOnce();
    return this.store.peekRecord('content-service/folder', folder_id);
  }

  resetController(controller: FolderController, isExiting: boolean) {
    if (isExiting) {
      this.knowledgeHubDrawerEditorService.removeEmberStickyQueryParams(controller);
    }
  }
}
